// src/components/Header.jsx
// Desktop: fixed top bar — logo | nav | bell | gear settings
// Mobile: fixed top bar — logo | bell | gear settings
//         fixed bottom bar — native app only (Discover | Play | Leagues | Rooms | Feed)
// Web mobile: "Get App" strip instead of tab bar
// ADDED: Ticker strip — pinned at top-56px, scrolling results + fixtures
// FIXED: transform: translate3d(0,0,0) on the fixed bars promotes them to
//        their own compositing layer so iOS WKWebView (with Capacitor
//        scrollEnabled:true + document-level native scrolling) keeps them
//        pinned to the viewport instead of repainting them at the document
//        bottom during scroll. backdrop-filter on a fixed element triggers
//        this iOS bug; the compositing-layer hint defeats it.

import React, { useState, useEffect, useRef } from "react";
import logo from "../assets/logo192.png";
import { Link, useLocation } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import { IonIcon } from "@ionic/react";
import { compassOutline, gameControllerOutline, trophyOutline, homeOutline, flashOutline } from "ionicons/icons";
import { useAuth } from "../Context/AuthContext";
import NotificationBell from "./NotificationBell";
import Ticker from "./Ticker";

// Web nav — app-only features (Play, Leagues, Rooms, Standings) excluded.
// Those live exclusively in the iOS app to drive downloads.
const NAV_LINKS = [
  { to: "/discover", label: "Discover" },
  { to: "/feed",     label: "Feed"     },
  { to: "/about",    label: "About"    },
];

const BOTTOM_TABS = [
  { to: "/discover", ionIcon: compassOutline,        label: "Discover" },
  { to: "/play",     ionIcon: gameControllerOutline,  label: "Play"     },
  { to: "/leagues",  ionIcon: trophyOutline,          label: "Leagues"  },
  { to: "/rooms",    ionIcon: homeOutline,             label: "Rooms"    },
  { to: "/feed",     ionIcon: flashOutline,            label: "Feed"     },
];

const ROLE_LABELS = {
  coach:    { label: "Coach",    cls: "bg-lime-400/20 text-lime-400 border-lime-400/30" },
  guardian: { label: "Guardian", cls: "bg-blue-400/20 text-blue-400 border-blue-400/30" },
  player:   { label: "Player",   cls: "bg-zinc-700 text-zinc-400 border-zinc-600"       },
};

// Compositing-layer hint for iOS WKWebView. Use this on every position:fixed
// element so the WebView pins its layer to the viewport during native scroll.
const FIX_LAYER = {
  transform:        "translate3d(0, 0, 0)",
  WebkitTransform:  "translate3d(0, 0, 0)",
  backfaceVisibility:       "hidden",
  WebkitBackfaceVisibility: "hidden",
};

function GearIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3"/>
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
    </svg>
  );
}

function SettingsPanel({ user, logout, isCoach, isGuardian, isAdmin, onClose }) {
  var roles      = Array.isArray(user.roles) ? user.roles : ["player"];
  var playerPath = "/player/" + encodeURIComponent(user.alias || "");

  // HashRouter app: navigate by setting the hash (e.g. "#/coach"), NOT
  // window.location.href = "/coach" — a bare path leaves the SPA, the server
  // serves index.html with an empty hash, and the router falls back to home.
  function go(path) { window.location.hash = path; onClose(); }

  return (
    <div className="absolute right-0 top-11 w-72 bg-zinc-900 border border-zinc-700 rounded-xl shadow-2xl z-[60] overflow-hidden">
      {/* User identity header */}
      <div className="px-4 py-4 border-b border-zinc-800 flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-lime-400/20 border border-lime-400/30 flex items-center justify-center flex-shrink-0">
          <span className="text-lime-400 font-bold text-base">
            {(user.alias || user.email || "?")[0].toUpperCase()}
          </span>
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-white font-semibold text-sm truncate">@{user.alias || "Player"}</p>
          <p className="text-zinc-500 text-[11px] truncate mt-0.5">{user.email}</p>
          <div className="flex flex-wrap gap-1 mt-1.5">
            {roles.map(function(r) {
              var cfg = ROLE_LABELS[r] || ROLE_LABELS.player;
              return (
                <span key={r} className={"text-[9px] font-mono uppercase px-1.5 py-0.5 rounded border " + cfg.cls}>
                  {cfg.label}
                </span>
              );
            })}
          </div>
        </div>
      </div>

      {/* Navigation items */}
      <div className="py-1">
        {/* Player profile — opens public-facing /player/:alias page */}
        <button onClick={function() { go(playerPath); }}
          className="w-full text-left px-4 py-2.5 text-sm text-zinc-300 hover:bg-zinc-800 hover:text-white transition-colors flex items-center gap-3">
          <span className="text-base w-5 text-center">{"\uD83D\uDC64"}</span><span>Player profile</span>
        </button>
        {isCoach && (
          <button onClick={function() { go("/coach"); }}
            className="w-full text-left px-4 py-2.5 text-sm text-zinc-300 hover:bg-zinc-800 hover:text-white transition-colors flex items-center gap-3">
            <span className="text-base w-5 text-center">{"\uD83C\uDFEB"}</span><span>Coach dashboard</span>
          </button>
        )}
        {isGuardian && (
          <button onClick={function() { go("/parent"); }}
            className="w-full text-left px-4 py-2.5 text-sm text-zinc-300 hover:bg-zinc-800 hover:text-white transition-colors flex items-center gap-3">
            <span className="text-base w-5 text-center">{"\uD83D\uDEE1\uFE0F"}</span><span>Parent dashboard</span>
          </button>
        )}
        {isAdmin && (
          <button onClick={function() { go("/admin/moderation"); }}
            className="w-full text-left px-4 py-2.5 text-sm text-red-400 hover:bg-red-900/20 hover:text-red-300 transition-colors flex items-center gap-3">
            <span className="text-base w-5 text-center">{"\uD83D\uDEE1\uFE0F"}</span><span>Moderation</span>
          </button>
        )}
      </div>

      {/* Account actions */}
      <div className="border-t border-zinc-800 py-1">
        <button onClick={function() { logout(); onClose(); }}
          className="w-full text-left px-4 py-2.5 text-sm text-red-400 hover:bg-red-900/20 hover:text-red-300 transition-colors flex items-center gap-3">
          <span className="text-base w-5 text-center">{"\uD83D\uDEAA"}</span><span>Log out</span>
        </button>
        <button onClick={function() { window.dispatchEvent(new Event("open-delete")); onClose(); }}
          className="w-full text-left px-4 py-2.5 text-xs text-zinc-600 hover:bg-zinc-800/60 hover:text-zinc-500 transition-colors flex items-center gap-3">
          <span className="text-sm w-5 text-center">{"\uD83D\uDDD1\uFE0F"}</span><span>Delete account</span>
        </button>
      </div>

      {/* Policy links — critical for App Review (Children's Privacy especially) */}
      <div className="border-t border-zinc-800 py-1">
        <button onClick={function() { go("/about"); }}
          className="w-full text-left px-4 py-2.5 text-xs text-zinc-600 hover:bg-zinc-800/60 hover:text-zinc-400 transition-colors flex items-center gap-3">
          <span className="text-sm w-5 text-center">{"\u2139\uFE0F"}</span><span>About Gametrait</span>
        </button>
        <button onClick={function() { go("/terms"); }}
          className="w-full text-left px-4 py-2.5 text-xs text-zinc-600 hover:bg-zinc-800/60 hover:text-zinc-400 transition-colors flex items-center gap-3">
          <span className="text-sm w-5 text-center">{"\uD83D\uDCCB"}</span><span>Terms of Service</span>
        </button>
        <button onClick={function() { go("/privacy-policy"); }}
          className="w-full text-left px-4 py-2.5 text-xs text-zinc-600 hover:bg-zinc-800/60 hover:text-zinc-400 transition-colors flex items-center gap-3">
          <span className="text-sm w-5 text-center">{"\uD83D\uDD12"}</span><span>Privacy Policy</span>
        </button>
        {/* Children's Privacy — one tap away for App Review compliance */}
        <button onClick={function() { go("/children-privacy"); }}
          className="w-full text-left px-4 py-2.5 text-xs text-zinc-600 hover:bg-zinc-800/60 hover:text-zinc-400 transition-colors flex items-center gap-3">
          <span className="text-sm w-5 text-center">{"\uD83D\uDC76"}</span><span>Children's Privacy</span>
        </button>
        <button onClick={function() { window.open("mailto:support@gametrait.com", "_blank"); onClose(); }}
          className="w-full text-left px-4 py-2.5 text-xs text-zinc-600 hover:bg-zinc-800/60 hover:text-zinc-400 transition-colors flex items-center gap-3">
          <span className="text-sm w-5 text-center">{"\u2709\uFE0F"}</span><span>Contact Us</span>
        </button>
        <button onClick={function() { window.open("mailto:report@gametrait.com?subject=Content%20Report", "_blank"); onClose(); }}
          className="w-full text-left px-4 py-2.5 text-xs text-zinc-600 hover:bg-zinc-800/60 hover:text-zinc-400 transition-colors flex items-center gap-3">
          <span className="text-sm w-5 text-center">{"\uD83D\uDEA9"}</span><span>Report Content</span>
        </button>
      </div>
    </div>
  );
}

function SettingsButton({ user, logout, isCoach, isGuardian, isAdmin }) {
  var [open, setOpen] = useState(false);
  var ref = useRef(null);

  useEffect(function() {
    if (!open) return;
    function onOutside(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onOutside);
    document.addEventListener("touchstart", onOutside, { passive: true });
    return function() {
      document.removeEventListener("mousedown", onOutside);
      document.removeEventListener("touchstart", onOutside);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={function() { setOpen(function(v) { return !v; }); }}
        aria-label="Settings"
        className={"w-8 h-8 flex items-center justify-center rounded-lg transition-colors " +
          (open ? "bg-zinc-800 text-white" : "text-zinc-400 hover:text-white hover:bg-zinc-800/60")}>
        <GearIcon />
      </button>
      {open && (
        <SettingsPanel user={user} logout={logout} isCoach={isCoach} isGuardian={isGuardian} isAdmin={isAdmin}
          onClose={function() { setOpen(false); }} />
      )}
    </div>
  );
}

export default function Header() {
  var { user, logout } = useAuth();
  var location = useLocation();

  var activeCls   = "text-white";
  var inactiveCls = "text-zinc-400 hover:text-white transition-colors text-xs tracking-[0.25em] uppercase font-medium";

  var isCoach    = !!(user && user.roles && user.roles.includes("coach"));
  var isGuardian = !!(user && user.roles && user.roles.includes("guardian"));
  var isAdmin    = !!(user && user.roles && user.roles.includes("admin"));

  function isActive(to) {
    if (to === "/") return location.pathname === "/";
    return location.pathname.startsWith(to);
  }

  return (
    <>
      {/* ── Top Header ──────────────────────────────────────────────── */}
      <header
        className="fixed top-0 w-full z-50 bg-black/85 backdrop-blur-md border-b border-white/10"
        style={Object.assign({ paddingTop: "env(safe-area-inset-top, 0px)" }, FIX_LAYER)}>
        <div className="max-w-7xl mx-auto px-5 h-14 flex items-center justify-between gap-6">

          {/* Logo */}
          <Link to="/" className="flex items-center gap-2.5 flex-shrink-0">
            <img src={logo} alt="Gametrait" className="w-7 h-7" />
            <div className="flex flex-col leading-none">
              <span className="text-white text-sm font-bold tracking-tight">Gametrait</span>
              <span className="text-zinc-500 text-[9px] font-mono uppercase tracking-[0.2em]">Intelligence</span>
            </div>
          </Link>

          {/* Desktop nav — web only shows Discover, Feed, About */}
          <nav className="hidden md:flex items-center gap-8 text-xs tracking-[0.25em] uppercase font-medium">
            {NAV_LINKS.map(function(l) {
              return (
                <Link key={l.to} to={l.to}
                  className={isActive(l.to) ? activeCls : inactiveCls}>
                  {l.label}
                </Link>
              );
            })}
          </nav>

          {/* Right side */}
          <div className="flex items-center gap-2">
            {user && <NotificationBell user={user} />}
            {user ? (
              <SettingsButton user={user} logout={logout} isCoach={isCoach} isGuardian={isGuardian} isAdmin={isAdmin} />
            ) : (
              <>
                <button onClick={function() { window.dispatchEvent(new Event("open-login")); }}
                  className={"hidden md:block " + inactiveCls}>Login</button>
                <button onClick={function() { window.dispatchEvent(new Event("open-login")); }}
                  className="md:hidden w-8 h-8 flex items-center justify-center text-zinc-400 hover:text-white transition-colors">
                  <span className="text-lg">{"\uD83D\uDC64"}</span>
                </button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* ── Ticker strip — pinned just below header ──────────────────── */}
      <Ticker />

      {/* ── Bottom navigation — native app gets tabs, web gets Get App strip ── */}
      {Capacitor.isNativePlatform() ? (
        <nav
          className="fixed bottom-0 left-0 right-0 z-40 md:hidden bg-black/95 backdrop-blur-md border-t border-zinc-800"
          style={Object.assign({ paddingBottom: "env(safe-area-inset-bottom, 0px)" }, FIX_LAYER)}>
          <div className="flex items-stretch">
            {BOTTOM_TABS.map(function(tab) {
              var active = isActive(tab.to);
              return (
                <Link key={tab.to} to={tab.to}
                  className={"flex-1 flex flex-col items-center justify-center py-2 gap-0.5 transition-colors min-w-0 " +
                    (active ? "text-lime-400" : "text-zinc-500 active:text-zinc-300")}>
                  <IonIcon icon={tab.ionIcon} style={{ fontSize: "22px", color: "inherit" }} />
                  <span className="text-[9px] font-mono uppercase tracking-tight w-full text-center leading-none whitespace-nowrap">
                    {tab.label}
                  </span>
                </Link>
              );
            })}
          </div>
        </nav>
      ) : (
        <div
          className="fixed bottom-0 left-0 right-0 z-40 md:hidden bg-black/95 backdrop-blur-md border-t border-zinc-800 px-4 py-3 flex items-center justify-between gap-3"
          style={Object.assign({ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 12px)" }, FIX_LAYER)}>
          <p className="text-zinc-400 text-xs">Play, Leagues &amp; Rooms are in the app</p>
          <a href="https://apps.apple.com/app/gametrait/id6742704573"
            className="bg-lime-400 hover:bg-lime-300 text-black text-[11px] font-bold px-3 py-1.5 rounded-lg transition-colors flex-shrink-0 whitespace-nowrap">
            Get App
          </a>
        </div>
      )}

      {/* Spacer so page content is not hidden behind the bottom bar */}
      <div className="md:hidden" style={{ height: "calc(3.25rem + env(safe-area-inset-bottom, 0px))" }} />

    </>
  );
}
